import React, { useEffect, useMemo, useState } from "react";
import { adminDashboard, adminBookings, adminEnquiries, adminUpdateEnquiryStatus, adminMarkContacted, adminResetDemo } from "@/lib/api";
import BrandHeading from "@/components/brand/BrandHeading";
import BrandLabel from "@/components/brand/BrandLabel";
import BrandButton from "@/components/brand/BrandButton";
import BrandCard from "@/components/brand/BrandCard";
import BrandInput from "@/components/brand/BrandInput";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHead, TableHeader, TableRow, TableCell, TableBody } from "@/components/ui/table";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, BarChart, Bar, CartesianGrid, Cell, PieChart, Pie, LineChart, Line, Legend } from "recharts";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BRAND_COLOR = { castlewood: "#5C2730", vela: "#E07856", brionj: "#C5B188" };

// ----- Dummy operator-insight data (reconciles with seeded ~€14.5k / 11 bookings period) -----
const ADR_REVPAR = [
  { month: "Aug", adr: 420, revpar: 145 },
  { month: "Sep", adr: 445, revpar: 158 },
  { month: "Oct", adr: 460, revpar: 170 },
  { month: "Nov", adr: 410, revpar: 138 },
  { month: "Dec", adr: 480, revpar: 178 },
  { month: "Jan", adr: 510, revpar: 195 },
];

const BOOKING_SOURCES = [
  { name: "Direct", value: 52, color: "#1E3A5F" },
  { name: "OTAs (Booking, Expedia)", value: 28, color: "#7A2828" },
  { name: "GDS", value: 12, color: "#E07856" },
  { name: "Travel agents", value: 8, color: "#C5B188" },
];

const LEAD_TIME = [
  { bucket: "0–7 days", value: 18 },
  { bucket: "8–30 days", value: 34 },
  { bucket: "31–90 days", value: 30 },
  { bucket: "90+ days", value: 18 },
];

const BRAND_PERF = [
  { brand: "Castlewood", color: "#7A2828", properties: 8, occupancy: 38, adr: 380, revpar: 144, yoy: "+12%" },
  { brand: "Vela", color: "#E07856", properties: 5, occupancy: 42, adr: 295, revpar: 124, yoy: "+7%" },
  { brand: "Brionj", color: "#C5B188", properties: 4, occupancy: 28, adr: null, revpar: null, yoy: "+24%" },
];

const GEO = [
  { country: "Germany", pct: 42 },
  { country: "United Kingdom", pct: 18 },
  { country: "United States", pct: 11 },
  { country: "Switzerland", pct: 8 },
  { country: "Austria", pct: 7 },
  { country: "Netherlands", pct: 5 },
  { country: "France", pct: 4 },
  { country: "Italy", pct: 3 },
  { country: "Other", pct: 2 },
];

const ACTIVITY = [
  { ago: "2 min", brand: "castlewood", title: "New booking", detail: "Hotel Royal · Anna Berg booked Belle Suite, 4 nights" },
  { ago: "18 min", brand: "brionj", title: "New enquiry", detail: "Villa Lisboa · Henrik Falk, wedding party 12" },
  { ago: "1 hr", brand: "castlewood", title: "Cancellation", detail: "Landgasthof Karner · Olaf Reuter cancelled 3-night stay" },
  { ago: "2 hr", brand: "castlewood", title: "Review posted", detail: "Schlosshotel Althörnitz · ★ 5/5 from Marta Klein" },
  { ago: "4 hr", brand: "vela", title: "New booking", detail: "the shoreline · Sophie Lang booked dune apartment, 5 nights" },
  { ago: "7 hr", brand: "brionj", title: "Enquiry contacted", detail: "Thakasile Lodge · Marta Klein notified of availability" },
  { ago: "11 hr", brand: "vela", title: "Check-in completed", detail: "LOEV · Mia Strand" },
  { ago: "14 hr", brand: "castlewood", title: "New booking", detail: "Hotel Schwarzwald · Lena Voss booked Garden Suite, 4 nights" },
  { ago: "18 hr", brand: "vela", title: "Review posted", detail: "Hotel Bernstein · ★ 4/5 from Erik Hansen" },
  { ago: "1 day", brand: "brionj", title: "New enquiry", detail: "Plant Villas · Mia Strand, corporate retreat 16" },
];

const ARRIVALS = [
  { date: "14 Jun", brand: "castlewood", property: "Hotel Royal", guest: "Henrik Falk", party: 2 },
  { date: "17 Jun", brand: "castlewood", property: "Landgasthof Zur Post", guest: "Lena Voss", party: 2 },
  { date: "19 Jun", brand: "vela", property: "Hotel Bernstein", guest: "Sophie Lang", party: 2 },
  { date: "22 Jun", brand: "vela", property: "the shoreline", guest: "Anna Berg", party: 2 },
  { date: "25 Jun", brand: "castlewood", property: "Hotel Schwarzwald", guest: "Marta Klein", party: 3 },
];

const BRIONJ_PIPELINE = [
  { stage: "New", items: [{ name: "Henrik Falk", residence: "Villa Lisboa" }, { name: "Mia Strand", residence: "Plant Villas" }, { name: "Lena Voss", residence: "Villa Bartolomeo" }] },
  { stage: "In Conversation", items: [{ name: "Marta Klein", residence: "Thakasile Lodge" }, { name: "Anna Berg", residence: "Plant Villas" }] },
  { stage: "Proposal Sent", items: [{ name: "Erik Hansen", residence: "Thakasile Lodge" }] },
  { stage: "Confirmed", items: [{ name: "Olaf Reuter", residence: "Plant Villas" }] },
];
const statusOrder = ["new", "in_review", "contacted", "closed"];
const statusLabel = { new: "New", in_review: "In review", contacted: "Contacted", closed: "Closed", converted: "Converted" };
const statusTone = {
  new: "bg-sky-50 text-sky-800 border-sky-200",
  in_review: "bg-amber-50 text-amber-800 border-amber-200",
  contacted: "bg-emerald-50 text-emerald-800 border-emerald-200",
  closed: "bg-slate-100 text-slate-700 border-slate-200",
  converted: "bg-emerald-50 text-emerald-800 border-emerald-200",
  confirmed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelled: "bg-rose-50 text-rose-800 border-rose-200",
};

const KPI = ({ label, value, hint }) => (
  <BrandCard className="p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
    <div className="mt-3 text-[28px] font-display">{value}</div>
    {hint && <div className="text-[12px] text-muted-foreground mt-1">{hint}</div>}
  </BrandCard>
);

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [bookingsFilter, setBookingsFilter] = useState({ brand: "all", status: "all", q: "" });

  const loadAll = async () => {
    const [d, b, e] = await Promise.all([adminDashboard(), adminBookings(), adminEnquiries()]);
    setData(d); setBookings(b); setEnquiries(e);
  };
  useEffect(() => { loadAll(); }, []);

  const filteredBookings = useMemo(() => {
    return bookings.filter((b) => {
      if (bookingsFilter.brand !== "all" && b.brand !== bookingsFilter.brand) return false;
      if (bookingsFilter.status !== "all" && b.status !== bookingsFilter.status) return false;
      if (bookingsFilter.q) {
        const q = bookingsFilter.q.toLowerCase();
        if (!(b.guest_name?.toLowerCase().includes(q) || b.property_name?.toLowerCase().includes(q) || b.guest_email?.toLowerCase().includes(q))) return false;
      }
      return true;
    });
  }, [bookings, bookingsFilter]);

  const enqByStatus = useMemo(() => {
    const map = Object.fromEntries(statusOrder.map((s) => [s, []]));
    enquiries.forEach((e) => { const s = e.status === "converted" ? "closed" : e.status; if (map[s]) map[s].push(e); });
    return map;
  }, [enquiries]);

  const moveStatus = async (id, status) => {
    try { await adminUpdateEnquiryStatus(id, status); toast(`Enquiry moved to ${statusLabel[status] || status}`); loadAll(); }
    catch { toast.error("Couldn't update status"); }
  };
  const markContacted = async (id) => { try { await adminMarkContacted(id); toast("Marked contacted"); loadAll(); } catch { toast.error("Failed"); } };
  const resetDemo = async () => { if (!window.confirm("Reset all demo data?")) return; try { await adminResetDemo(); toast.success("Demo data reset"); loadAll(); } catch { toast.error("Reset failed"); } };

  if (!data) return <div className="container-rail py-12">Loading…</div>;

  return (
    <div className="container-rail py-8 md:py-12" data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <BrandLabel>Operator</BrandLabel>
          <BrandHeading level={1} className="mt-2">Dashboard</BrandHeading>
          <p className="mt-2 text-[14px] text-muted-foreground">A consolidated view across Castlewood, Vela and Brionj.</p>
        </div>
        <BrandButton variant="secondary" onClick={resetDemo} data-testid="admin-reset">Reset demo data</BrandButton>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPI label="Revenue" value={`€${Math.round(data.kpis.total_revenue).toLocaleString()}`} hint="This period" />
        <KPI label="Bookings" value={data.kpis.bookings_total} hint="Confirmed" />
        <KPI label="Cancelled" value={data.kpis.bookings_cancelled} hint="Last 90 days" />
        <KPI label="Enquiries open" value={data.kpis.enquiries_open} hint="Brionj" />
        <KPI label="Avg occupancy" value={`${data.kpis.occupancy_avg}%`} hint="Rolling" />
        <KPI label="Brands" value="3" hint="Active in portfolio" />
      </div>

      <div className="mt-8 grid lg:grid-cols-[1.5fr_1fr] gap-6">
        <BrandCard className="p-5">
          <div className="flex items-center justify-between"><BrandLabel>Revenue trend</BrandLabel><span className="text-[12px] text-muted-foreground">Last 6 months</span></div>
          <div className="mt-4 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.revenue_trend} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="revenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="1" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `€${v / 1000}k`} />
                <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" fill="url(#revenue)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </BrandCard>
        <BrandCard className="p-5">
          <BrandLabel>Bookings by brand</BrandLabel>
          <div className="mt-4 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.brand_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="brand" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `€${v / 1000}k`} />
                <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>{data.brand_revenue.map((b) => (<Cell key={b.brand} fill={BRAND_COLOR[b.brand] || "#1E3A5F"} />))}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BrandCard>
      </div>

      {/* ================================================================
          NEW OPERATOR INSIGHT MODULES — additive, dummy data, reconciled
          ================================================================ */}
      <div className="mt-12 mb-4 flex items-end justify-between">
        <BrandLabel>Operator insights</BrandLabel>
        <span className="text-[12px] text-muted-foreground">Indicative figures · this period</span>
      </div>

      {/* Row 1 — Financial: ADR & RevPAR trend  +  Booking source breakdown */}
      <div className="grid lg:grid-cols-3 gap-6">
        <BrandCard className="p-5 lg:col-span-2" data-testid="insight-adr-revpar">
          <div className="flex items-center justify-between">
            <BrandLabel>ADR &amp; RevPAR trend</BrandLabel>
            <span className="text-[12px] text-muted-foreground">Last 6 months · EUR</span>
          </div>
          <div className="mt-4 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={ADR_REVPAR} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `€${v}`} />
                <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => `€${v}`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="adr" name="ADR" stroke="#1E3A5F" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="revpar" name="RevPAR" stroke="#E07856" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </BrandCard>

        <BrandCard className="p-5" data-testid="insight-booking-sources">
          <BrandLabel>Booking source</BrandLabel>
          <div className="mt-3 h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={BOOKING_SOURCES} dataKey="value" nameKey="name" innerRadius={42} outerRadius={72} paddingAngle={2} stroke="hsl(var(--card))" strokeWidth={2}>
                  {BOOKING_SOURCES.map((s) => <Cell key={s.name} fill={s.color} />)}
                </Pie>
                <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => `${v}%`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-1.5 text-[12px]">
            {BOOKING_SOURCES.map((s) => (
              <div key={s.name} className="flex items-center justify-between">
                <span className="flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{background: s.color}} />{s.name}</span>
                <span className="font-medium">{s.value}%</span>
              </div>
            ))}
          </div>
        </BrandCard>
      </div>

      {/* Row 2 — Operational: Lead time + Brand performance comparison */}
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <BrandCard className="p-5" data-testid="insight-lead-time">
          <BrandLabel>Lead time distribution</BrandLabel>
          <div className="mt-4 h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={LEAD_TIME} layout="vertical" margin={{ left: 16, right: 20, top: 5, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `${v}%`} domain={[0, 50]} />
                <YAxis dataKey="bucket" type="category" stroke="hsl(var(--muted-foreground))" fontSize={12} width={90} />
                <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => `${v}%`} />
                <Bar dataKey="value" fill="#1E3A5F" radius={[0, 3, 3, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BrandCard>

        <BrandCard className="p-5" data-testid="insight-brand-performance">
          <BrandLabel>Brand performance</BrandLabel>
          <div className="mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Brand</TableHead>
                  <TableHead>Properties</TableHead>
                  <TableHead>Occupancy</TableHead>
                  <TableHead>ADR</TableHead>
                  <TableHead>RevPAR</TableHead>
                  <TableHead>YoY</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {BRAND_PERF.map((b) => (
                  <TableRow key={b.brand}>
                    <TableCell>
                      <span className="inline-flex items-center gap-2 font-medium">
                        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{background: b.color}} />
                        {b.brand}
                      </span>
                    </TableCell>
                    <TableCell>{b.properties}</TableCell>
                    <TableCell>{b.occupancy}%</TableCell>
                    <TableCell className="text-[13px]">{b.adr ? `€${b.adr}` : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-[13px]">{b.revpar ? `€${b.revpar}` : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-emerald-700 font-medium">{b.yoy}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground italic">Brionj operates by enquiry — ADR / RevPAR not applicable.</p>
        </BrandCard>
      </div>

      {/* Row 3 — Guest data: Geographic distribution + Recent activity feed */}
      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <BrandCard className="p-5" data-testid="insight-geo">
          <BrandLabel>Guest origin</BrandLabel>
          <div className="mt-4 space-y-3">
            {GEO.map((g) => (
              <div key={g.country}>
                <div className="flex justify-between text-[13px] mb-1">
                  <span>{g.country}</span>
                  <span className="font-medium">{g.pct}%</span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-[#1E3A5F]" style={{width: `${Math.min(100, g.pct * 2.2)}%`}} />
                </div>
              </div>
            ))}
          </div>
        </BrandCard>

        <BrandCard className="p-5 lg:col-span-2" data-testid="insight-activity">
          <div className="flex items-center justify-between">
            <BrandLabel>Recent activity</BrandLabel>
            <span className="text-[12px] text-muted-foreground">Last 24 hours</span>
          </div>
          <div className="mt-5 relative pl-6">
            <div className="absolute top-2 bottom-2 left-2 w-px bg-border" />
            <div className="space-y-4">
              {ACTIVITY.map((a, i) => (
                <div key={`${a.title}-${a.ago}`} className="relative" data-testid={`activity-row-${i}`}>
                  <span className="absolute -left-[18px] top-1.5 w-2.5 h-2.5 rounded-full ring-2 ring-background" style={{background: BRAND_COLOR[a.brand]}} />
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[13px] font-medium">{a.title}</span>
                    <Badge variant="outline" className="text-[10px]" style={{borderColor: BRAND_COLOR[a.brand]}}>{a.brand}</Badge>
                    <span className="text-[12px] text-muted-foreground ml-auto">{a.ago} ago</span>
                  </div>
                  <div className="mt-1 text-[12px] text-muted-foreground">{a.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </BrandCard>
      </div>

      {/* Row 4 — Operations: Upcoming arrivals + Brionj enquiry pipeline */}
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <BrandCard className="p-5" data-testid="insight-arrivals">
          <div className="flex items-center justify-between">
            <BrandLabel>Upcoming arrivals</BrandLabel>
            <span className="text-[12px] text-muted-foreground">Next five</span>
          </div>
          <div className="mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Guest</TableHead>
                  <TableHead>Party</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ARRIVALS.map((a, i) => (
                  <TableRow key={`${a.date}-${a.guest}`} data-testid={`arrival-row-${i}`}>
                    <TableCell className="font-medium text-[13px]">{a.date}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]" style={{borderColor: BRAND_COLOR[a.brand]}}>{a.brand}</Badge></TableCell>
                    <TableCell className="text-[13px]">{a.property}</TableCell>
                    <TableCell className="text-[13px]">{a.guest}</TableCell>
                    <TableCell className="text-[13px]">{a.party}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </BrandCard>

        <BrandCard className="p-5" data-testid="insight-brionj-pipeline">
          <div className="flex items-center justify-between">
            <BrandLabel>Brionj enquiry pipeline</BrandLabel>
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground" style={{color: "#C5B188"}}>By enquiry</span>
          </div>
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-2">
            {BRIONJ_PIPELINE.map((col) => (
              <div key={col.stage} className="border border-border p-3 min-h-[150px]" data-testid={`pipeline-col-${col.stage.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{col.stage}</span>
                  <span className="text-[11px] font-medium text-foreground">{col.items.length}</span>
                </div>
                <div className="mt-3 space-y-2">
                  {col.items.map((it) => (
                    <div key={`${col.stage}-${it.name}`} className="text-[11px] border border-border bg-card p-2">
                      <div className="font-medium">{it.name}</div>
                      <div className="text-muted-foreground text-[10px] italic mt-0.5">{it.residence}</div>
                    </div>
                  ))}
                  {col.items.length === 0 && <div className="text-[11px] text-muted-foreground italic text-center py-2">—</div>}
                </div>
              </div>
            ))}
          </div>
        </BrandCard>
      </div>

      <Tabs defaultValue="bookings" className="mt-12">
        <TabsList>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings ({bookings.length})</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
          <TabsTrigger value="occupancy" data-testid="admin-tab-occupancy">Occupancy</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings" className="mt-6">
          <BrandCard className="p-5">
            <div className="grid sm:grid-cols-3 gap-3 mb-5">
              <BrandInput placeholder="Search name, email, property…" value={bookingsFilter.q} onChange={(e) => setBookingsFilter({ ...bookingsFilter, q: e.target.value })} data-testid="admin-search-bookings" />
              <Select value={bookingsFilter.brand} onValueChange={(v) => setBookingsFilter({ ...bookingsFilter, brand: v })}><SelectTrigger data-testid="admin-filter-brand"><SelectValue placeholder="Brand" /></SelectTrigger><SelectContent><SelectItem value="all">All brands</SelectItem><SelectItem value="castlewood">Castlewood</SelectItem><SelectItem value="vela">Vela</SelectItem></SelectContent></Select>
              <Select value={bookingsFilter.status} onValueChange={(v) => setBookingsFilter({ ...bookingsFilter, status: v })}><SelectTrigger data-testid="admin-filter-status"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="confirmed">Confirmed</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent></Select>
            </div>
            <div className="overflow-x-auto">
              <Table data-testid="admin-bookings-table">
                <TableHeader><TableRow><TableHead>Guest</TableHead><TableHead>Property</TableHead><TableHead>Brand</TableHead><TableHead>Dates</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {filteredBookings.map((b) => (
                    <TableRow key={b.id} data-testid={`admin-row-${b.id}`}>
                      <TableCell><div className="font-medium">{b.guest_name}</div><div className="text-[12px] text-muted-foreground">{b.guest_email}</div></TableCell>
                      <TableCell><div>{b.property_name}</div><div className="text-[12px] text-muted-foreground">{b.room_name}</div></TableCell>
                      <TableCell><Badge variant="outline" style={{ borderColor: BRAND_COLOR[b.brand] }}>{b.brand}</Badge></TableCell>
                      <TableCell className="text-[13px]">{b.check_in} → {b.check_out}<div className="text-[12px] text-muted-foreground">{b.nights}n</div></TableCell>
                      <TableCell className="font-medium">€{Math.round(b.total)}</TableCell>
                      <TableCell><Badge className={cn("border", statusTone[b.status])}>{b.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredBookings.length === 0 && <div className="py-8 text-center text-muted-foreground text-[13px]">No bookings match.</div>}
            </div>
          </BrandCard>
        </TabsContent>

        <TabsContent value="enquiries" className="mt-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-kanban">
            {statusOrder.map((s) => (
              <BrandCard key={s} className="p-4" data-testid={`kanban-col-${s}`}>
                <div className="flex items-center justify-between mb-3"><h3 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground" data-testid={`kanban-heading-${s}`}>{statusLabel[s]}</h3><span className="text-[12px] text-muted-foreground">{enqByStatus[s].length}</span></div>
                <div className="space-y-3 min-h-[120px]">
                  {enqByStatus[s].map((e) => (
                    <div key={e.id} className="border border-border rounded-[var(--radius)] p-3 bg-card" data-testid={`kanban-card-${e.id}`}>
                      <div className="font-medium text-[14px]">{e.guest_name}</div>
                      <div className="text-[12px] text-muted-foreground">{e.residence_name}</div>
                      <div className="text-[11px] mt-1 text-muted-foreground">{e.guests} guests · {e.bedrooms} bedrooms</div>
                      {e.notes && <p className="mt-2 text-[12px] line-clamp-2">{e.notes}</p>}
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <Select value={s} onValueChange={(v) => moveStatus(e.id, v)}>
                          <SelectTrigger className="h-8 text-[11px]" data-testid={`status-${e.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{statusOrder.map((sv) => <SelectItem key={sv} value={sv}>{statusLabel[sv]}</SelectItem>)}</SelectContent>
                        </Select>
                        {s !== "contacted" && <button onClick={() => markContacted(e.id)} className="text-[11px] underline" data-testid={`contact-${e.id}`}>Mark contacted</button>}
                      </div>
                    </div>
                  ))}
                  {enqByStatus[s].length === 0 && <div className="text-[12px] text-muted-foreground py-4 text-center">No enquiries</div>}
                </div>
              </BrandCard>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="occupancy" className="mt-6">
          <BrandCard className="p-5">
            <BrandLabel>Property occupancy (rolling 90d)</BrandLabel>
            <div className="mt-4 grid md:grid-cols-2 gap-4" data-testid="admin-occupancy">
              {data.occupancy.map((o) => (
                <div key={o.property_id} className="border border-border p-4">
                  <div className="flex items-center justify-between"><div className="text-[14px] font-medium">{o.property_name}</div><Badge variant="outline" style={{ borderColor: BRAND_COLOR[o.brand] }}>{o.brand}</Badge></div>
                  <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden"><div className="h-full" style={{ width: `${o.occupancy_pct}%`, background: BRAND_COLOR[o.brand] || "#1E3A5F" }} /></div>
                  <div className="mt-2 flex justify-between text-[12px] text-muted-foreground"><span>{o.occupancy_pct}% occupied</span><span>{o.nights_booked} nights · €{Math.round(o.revenue).toLocaleString()}</span></div>
                </div>
              ))}
            </div>
          </BrandCard>
        </TabsContent>
      </Tabs>
    </div>
  );
};
export default AdminDashboard;
